import { IconArrowLeft } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";
import { Chart } from "@/components/mdx/Chart";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { config } from "@/data/config";
import { getSolarDataPageFn, type SolarPageData } from "@/server/solar-stats";
import { schema, toScript } from "@/utils/schema";
import { seo } from "@/utils/seo";

const GENERATED_RATE_PER_KWH = 0.346;
const EXPORTED_RATE_PER_KWH = 0.24;
const SOLAR_SYSTEM_COST_USD = 48_525.66;
const PAYBACK_LOOKBACK_DAYS = 30;

function toKwh(wh: number): number {
	return Math.round((wh / 1000) * 100) / 100;
}

function toUsd(value: number): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 0,
	}).format(value);
}

function pct(value: number | null): string {
	if (value === null || Number.isNaN(value)) return "n/a";
	return `${Math.round(value * 100)}%`;
}

type SolarLoaderData = SolarPageData;

const EMPTY_DATA: SolarLoaderData = {
	rows: [],
	recentRows: [],
	monthlyRows: [],
	weeklyRows: [],
	summary: {
		days: 0,
		productionWh: 0,
		consumptionWh: 0,
		importedWh: 0,
		exportedWh: 0,
	},
	page: 1,
	pageSize: 30,
	totalRows: 0,
	totalPages: 1,
	totalMonths: 0,
	monthPage: 1,
	monthPageSize: 12,
	monthTotalPages: 1,
};

type StatItem = {
	label: string;
	value: string;
};

function StatGrid({ stats }: { stats: StatItem[] }) {
	return (
		<div className="@container/stats">
			<dl className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-border/70 bg-border/70 sm:grid-cols-2 lg:grid-cols-4">
				{stats.map((stat) => (
					<div key={stat.label} className="bg-card px-6 py-5">
						<dt className="truncate text-sm text-muted-foreground">
							{stat.label}
						</dt>
						<dd className="mt-2 text-2xl font-semibold tracking-tight tabular-nums">
							{stat.value}
						</dd>
					</div>
				))}
			</dl>
		</div>
	);
}

export const Route = createFileRoute("/_pages/data/solar")({
	component: RouteComponent,
	loader: async () =>
		getSolarDataPageFn({
			data: { page: 1, pageSize: 30, monthPage: 1, monthPageSize: 12 },
		}),
	head: () => {
		const description =
			"Historical home solar generation and grid usage data collected nightly from Enphase.";
		const base = seo({
			title: `Solar | ${config.Name}`,
			description,
			keywords:
				"solar, enphase, home energy, electricity usage, grid dependency, data",
			url: `${config.Url}/data/solar`,
		});
		return {
			...base,
			scripts: [
				...base.scripts,
				toScript(
					schema.collectionPage(
						`${config.Name} - Solar`,
						`${config.Url}/data/solar`,
						description,
					),
				),
				toScript(
					schema.breadcrumb([
						{ name: config.Name, url: config.Url },
						{ name: "Data", url: `${config.Url}/data` },
						{ name: "Solar", url: `${config.Url}/data/solar` },
					]),
				),
			],
		};
	},
});

function RouteComponent() {
	const initial = Route.useLoaderData();
	const [data, setData] = useState<SolarLoaderData>(initial);
	const [chartMode, setChartMode] = useState<"energy" | "money">("energy");
	const [energyGranularity, setEnergyGranularity] = useState<
		"daily" | "weekly" | "monthly"
	>("daily");
	const [page, setPage] = useState(1);
	const [monthPage, setMonthPage] = useState(1);
	const [isLoadingPage, setIsLoadingPage] = useState(false);
	const [paybackBarReady, setPaybackBarReady] = useState(false);
	const rows = data.rows ?? [];
	const recentRows = data.recentRows ?? [];
	const monthlyRows = data.monthlyRows ?? [];
	const weeklyRows = data.weeklyRows ?? [];
	const summary = data.summary ?? EMPTY_DATA.summary;
	const skipInitialFetch = useRef(true);

	useEffect(() => {
		const id = requestAnimationFrame(() => setPaybackBarReady(true));
		return () => cancelAnimationFrame(id);
	}, []);

	useEffect(() => {
		if (skipInitialFetch.current) {
			skipInitialFetch.current = false;
			if (page === 1 && monthPage === 1) return;
		}

		let cancelled = false;
		void (async () => {
			try {
				setIsLoadingPage(true);
				const next = await getSolarDataPageFn({
					data: {
						page,
						pageSize: 30,
						monthPage,
						monthPageSize: 12,
					},
				});
				if (!cancelled) setData(next);
			} catch {
				// Keep current state if the server fn fails.
			} finally {
				if (!cancelled) setIsLoadingPage(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [page, monthPage]);

	const latest = recentRows.at(-1) ?? null;
	const chartData = rows.map((row) => ({
		date: row.localDate.slice(5),
		generationKwh: toKwh(row.productionWh),
		gridImportKwh: toKwh(row.importedWh ?? 0),
		gridExportKwh: toKwh(row.exportedWh ?? 0),
	}));
	const weeklyEnergyChartData = weeklyRows.map((row) => ({
		week: row.week.slice(2),
		generationKwh: toKwh(row.productionWh),
		gridImportKwh: toKwh(row.importedWh),
		gridExportKwh: toKwh(row.exportedWh),
	}));
	const monthlyEnergyChartData = monthlyRows.map((row) => ({
		month: row.month.slice(2),
		generationKwh: toKwh(row.productionWh),
		gridImportKwh: toKwh(row.importedWh),
		gridExportKwh: toKwh(row.exportedWh),
	}));
	const energyChartDataset =
		energyGranularity === "weekly"
			? weeklyEnergyChartData
			: energyGranularity === "monthly"
				? monthlyEnergyChartData
				: chartData;
	const energyChartXKey =
		energyGranularity === "weekly"
			? "week"
			: energyGranularity === "monthly"
				? "month"
				: "date";
	const energyChartTitle =
		energyGranularity === "weekly"
			? "Generation vs Grid Import/Export (kWh) — week over week (last 30 weeks)"
			: energyGranularity === "monthly"
				? "Generation vs Grid Import/Export (kWh) — month over month (last 12 months)"
				: "Generation vs Grid Import/Export (kWh) — daily";
	const hasHistory = (summary?.days ?? 0) > 0;
	const recentValueSeries = recentRows.map((row) => {
		const producedKwh = row.productionWh / 1000;
		const exportedKwh = Math.max(0, (row.exportedWh ?? 0) / 1000);
		const selfConsumedKwh = Math.max(0, producedKwh - exportedKwh);
		const generatedValueUsd = selfConsumedKwh * GENERATED_RATE_PER_KWH;
		const exportedValueUsd = exportedKwh * EXPORTED_RATE_PER_KWH;
		return {
			date: row.localDate.slice(5),
			generatedValueUsd: Math.round(generatedValueUsd * 100) / 100,
			exportedValueUsd: Math.round(exportedValueUsd * 100) / 100,
			totalValueUsd:
				Math.round((generatedValueUsd + exportedValueUsd) * 100) / 100,
		};
	});
	const lifetimeProducedKwh = Math.max(0, summary.productionWh / 1000);
	const lifetimeExportedKwh = Math.max(0, summary.exportedWh / 1000);
	const lifetimeSelfConsumedKwh = Math.max(
		0,
		lifetimeProducedKwh - lifetimeExportedKwh,
	);
	const lifetimeEarnedUsd =
		lifetimeSelfConsumedKwh * GENERATED_RATE_PER_KWH +
		lifetimeExportedKwh * EXPORTED_RATE_PER_KWH;
	const remainingUsd = Math.max(0, SOLAR_SYSTEM_COST_USD - lifetimeEarnedUsd);
	const isPaidOff = remainingUsd <= 0;
	const paybackProgress = Math.min(
		100,
		(lifetimeEarnedUsd / SOLAR_SYSTEM_COST_USD) * 100,
	);
	const recentWindowTotalUsd = recentValueSeries.reduce(
		(sum, row) => sum + row.totalValueUsd,
		0,
	);
	let rollingRemaining = Math.max(0, remainingUsd + recentWindowTotalUsd);
	const balanceTrendData = recentValueSeries.map((row) => {
		rollingRemaining = Math.max(0, rollingRemaining - row.totalValueUsd);
		return { date: row.date, remainingUsd: rollingRemaining };
	});
	const monthlyFinanceChartData = monthlyRows.map((row) => {
		const producedKwh = Math.max(0, row.productionWh / 1000);
		const exportedKwh = Math.max(0, row.exportedWh / 1000);
		const selfConsumedKwh = Math.max(0, producedKwh - exportedKwh);
		const generatedValueUsd = selfConsumedKwh * GENERATED_RATE_PER_KWH;
		const exportedValueUsd = exportedKwh * EXPORTED_RATE_PER_KWH;
		const totalValueUsd = generatedValueUsd + exportedValueUsd;
		const previousMonthTotal =
			monthlyRows[monthlyRows.indexOf(row) - 1] === undefined
				? null
				: (() => {
						const prev = monthlyRows[monthlyRows.indexOf(row) - 1];
						const prevProduced = Math.max(0, prev.productionWh / 1000);
						const prevExported = Math.max(0, prev.exportedWh / 1000);
						const prevSelfConsumed = Math.max(0, prevProduced - prevExported);
						return (
							prevSelfConsumed * GENERATED_RATE_PER_KWH +
							prevExported * EXPORTED_RATE_PER_KWH
						);
					})();
		return {
			month: row.month.slice(2),
			generatedValueUsd: Math.round(generatedValueUsd * 100) / 100,
			exportedValueUsd: Math.round(exportedValueUsd * 100) / 100,
			totalValueUsd: Math.round(totalValueUsd * 100) / 100,
			monthOverMonthPct:
				previousMonthTotal && previousMonthTotal > 0
					? Math.round(
							((totalValueUsd - previousMonthTotal) / previousMonthTotal) * 100,
						)
					: 0,
		};
	});
	const recentFinanceWindow = recentValueSeries.slice(-PAYBACK_LOOKBACK_DAYS);
	const recentDailyAverageUsd =
		recentFinanceWindow.length > 0
			? recentFinanceWindow.reduce((sum, row) => sum + row.totalValueUsd, 0) /
				recentFinanceWindow.length
			: 0;
	const projectedDaysToPayoff =
		!isPaidOff && recentDailyAverageUsd > 0
			? Math.ceil(remainingUsd / recentDailyAverageUsd)
			: null;
	const projectedPayoffDate =
		projectedDaysToPayoff !== null
			? new Date(Date.now() + projectedDaysToPayoff * 24 * 60 * 60 * 1000)
			: null;
	const balanceValues = balanceTrendData.map((row) => row.remainingUsd);
	const balanceMin =
		balanceValues.length > 0 ? Math.min(...balanceValues) : remainingUsd;
	const balanceMax =
		balanceValues.length > 0 ? Math.max(...balanceValues) : remainingUsd;
	const balanceSpan = Math.max(1, balanceMax - balanceMin);
	const miniChartDomain: [number, number] = [
		Math.max(0, balanceMin - balanceSpan * 0.2),
		balanceMax + balanceSpan * 0.2,
	];

	const stats: StatItem[] = [
		{
			label: "Yesterday generation",
			value: latest
				? `${toKwh(latest.productionWh).toLocaleString()} kWh`
				: "n/a",
		},
		{
			label: "Lifetime generation",
			value: `${toKwh(summary.productionWh).toLocaleString()} kWh`,
		},
		{
			label: "Latest grid dependency",
			value: latest ? pct(latest.gridDependencyRatio) : "n/a",
		},
		{
			label: "Lifetime value earned",
			value: toUsd(lifetimeEarnedUsd),
		},
	];

	return (
		<div className="relative bg-background">
			<div
				className="surface-grid pointer-events-none absolute inset-0 opacity-40"
				aria-hidden
			/>
			<main className="relative pt-12 pb-20 sm:pt-16">
				<div className="mx-auto max-w-6xl px-4 lg:px-8">
					<Link
						to="/data"
						className="mb-8 inline-flex animate-in items-center gap-1.5 text-sm text-muted-foreground duration-700 fill-mode-both fade-in slide-in-from-bottom-4 hover:text-foreground"
						style={{ animationDelay: "0.05s" }}
					>
						<IconArrowLeft className="size-4 shrink-0" aria-hidden />
						Back to data
					</Link>

					<div className="animate-fade-in-up">
						<MarketingHeader
							title="Enphase"
							heading="Solar"
							description="Solar generation, grid import/export, and consumption trends from Enphase."
						/>
					</div>

					<div
						className="animate-in duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
						style={{ animationDelay: "0.18s" }}
					>
						<StatGrid stats={stats} />
					</div>

					<div
						className="relative mt-6 animate-in overflow-hidden rounded-xl border border-border/70 bg-card duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
						style={{ animationDelay: "0.3s" }}
					>
						<div className="pointer-events-none absolute inset-0 opacity-30">
							<ResponsiveContainer width="100%" height="100%">
								<AreaChart
									data={balanceTrendData}
									margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
								>
									<YAxis hide domain={miniChartDomain} />
									<Area
										type="monotone"
										dataKey="remainingUsd"
										stroke="var(--chart-1)"
										fill="var(--chart-1)"
										fillOpacity={0.22}
										strokeWidth={2}
										dot={false}
										isAnimationActive={false}
									/>
								</AreaChart>
							</ResponsiveContainer>
						</div>
						<div className="relative z-10 p-6 sm:p-8">
							<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
								<div>
									<p className="text-sm text-muted-foreground">
										Solar cost balance
									</p>
									<p className="mt-2 text-4xl font-semibold tracking-tight tabular-nums">
										{isPaidOff ? toUsd(0) : toUsd(remainingUsd)}
									</p>
									{isPaidOff ? (
										<p className="mt-2 max-w-[56ch] text-sm text-pretty text-muted-foreground">
											System cost is fully paid back.
										</p>
									) : null}
								</div>
								{isPaidOff ? (
									<Badge variant="success">Paid off</Badge>
								) : projectedPayoffDate ? (
									<div className="rounded-lg border border-border/70 bg-muted/40 px-4 py-3 text-sm">
										<p className="font-medium text-foreground">
											Estimated breakeven
										</p>
										<p className="mt-1 text-muted-foreground tabular-nums">
											{projectedPayoffDate.toLocaleDateString("en-US", {
												month: "short",
												day: "numeric",
												year: "numeric",
											})}
										</p>
										<p className="mt-1 text-muted-foreground">
											{projectedDaysToPayoff} days at{" "}
											{toUsd(recentDailyAverageUsd)}/day avg over last{" "}
											{Math.min(
												PAYBACK_LOOKBACK_DAYS,
												recentFinanceWindow.length,
											)}{" "}
											days.
										</p>
									</div>
								) : null}
							</div>

							<div className="mt-8">
								<div className="flex items-baseline justify-between gap-4 text-sm text-muted-foreground">
									<span className="tabular-nums">
										{toUsd(lifetimeEarnedUsd)} recovered
									</span>
									<span className="tabular-nums">
										{toUsd(SOLAR_SYSTEM_COST_USD)} system cost
									</span>
								</div>
								<div
									className="mt-2 h-2 overflow-hidden rounded-full bg-muted"
									role="progressbar"
									aria-valuenow={Math.round(paybackProgress)}
									aria-valuemin={0}
									aria-valuemax={100}
									aria-label="Payback progress"
								>
									<div
										className="h-full w-(--progress) rounded-full bg-primary transition-[width] duration-1000 ease-out"
										style={
											{
												"--progress": paybackBarReady
													? `${paybackProgress}%`
													: "0%",
											} as React.CSSProperties
										}
									/>
								</div>
							</div>

							<p className="mt-4 text-sm text-muted-foreground">
								Generated value: ${GENERATED_RATE_PER_KWH.toFixed(3)}/kWh
								{" · "}
								Export value: ${EXPORTED_RATE_PER_KWH.toFixed(2)}/kWh
							</p>
						</div>
					</div>

					{hasHistory ? (
						<section
							className="mt-12 animate-in border-t border-border/70 pt-12 duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
							style={{ animationDelay: "0.42s" }}
						>
							<h2 className="text-2xl font-semibold tracking-tight text-balance">
								History
							</h2>
							<p className="mt-2 max-w-[56ch] text-base text-pretty text-muted-foreground">
								Explore energy and financial trends, then browse daily records
								below.
							</p>

							<div className="mt-8 flex flex-col gap-4">
								<Tabs
									value={chartMode}
									onValueChange={(value) =>
										setChartMode(value as "energy" | "money")
									}
								>
									<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
										<TabsList>
											<TabsTrigger value="energy">Energy</TabsTrigger>
											<TabsTrigger value="money">Money</TabsTrigger>
										</TabsList>

										{chartMode === "energy" ? (
											<Tabs
												value={energyGranularity}
												onValueChange={(value) =>
													setEnergyGranularity(
														value as "daily" | "weekly" | "monthly",
													)
												}
											>
												<TabsList variant="line" className="max-w-full">
													<TabsTrigger value="daily">Daily</TabsTrigger>
													<TabsTrigger
														value="weekly"
														disabled={weeklyEnergyChartData.length === 0}
													>
														Week over week
													</TabsTrigger>
													<TabsTrigger
														value="monthly"
														disabled={monthlyEnergyChartData.length === 0}
													>
														Month over month
													</TabsTrigger>
												</TabsList>
											</Tabs>
										) : null}
									</div>
								</Tabs>

								{chartMode === "energy" && energyGranularity === "weekly" ? (
									<p className="text-sm text-muted-foreground">
										Weekly totals use ISO calendar weeks (
										{weeklyEnergyChartData.length} of 30 buckets shown).
									</p>
								) : null}
								{chartMode === "energy" && energyGranularity === "monthly" ? (
									<p className="text-sm text-muted-foreground">
										Monthly totals follow your Enphase nightly roll-ups; use
										paging below for older months.
									</p>
								) : null}
							</div>

							<Chart
								type="line"
								className="mt-2"
								title={
									chartMode === "energy"
										? energyChartTitle
										: "Month-over-month value (trailing 12 months)"
								}
								data={
									chartMode === "energy"
										? energyChartDataset
										: monthlyFinanceChartData
								}
								xKey={chartMode === "energy" ? energyChartXKey : "month"}
								series={
									chartMode === "energy"
										? [
												{ dataKey: "generationKwh", name: "Generation" },
												{ dataKey: "gridImportKwh", name: "Grid import" },
												{ dataKey: "gridExportKwh", name: "Grid export" },
											]
										: [
												{
													dataKey: "generatedValueUsd",
													name: "Generated value",
												},
												{
													dataKey: "exportedValueUsd",
													name: "Exported value",
												},
												{
													dataKey: "totalValueUsd",
													name: "Total value",
												},
											]
								}
								formatCurrency={chartMode === "money"}
								height={320}
							/>

							<h3 className="mt-10 text-lg font-semibold tracking-tight">
								Daily records
							</h3>

							<div className="-mx-4 -my-2 mt-4 overflow-x-auto whitespace-nowrap sm:-mx-6 lg:-mx-8">
								<div className="inline-block min-w-full px-4 py-2 align-middle sm:px-6 lg:px-8">
									<table className="w-full text-sm">
										<thead>
											<tr className="border-b border-border/70">
												<th className="pr-4 pb-3 text-left font-medium whitespace-nowrap text-muted-foreground">
													Date
												</th>
												<th className="pr-4 pb-3 text-left font-medium whitespace-nowrap text-muted-foreground">
													Generation (kWh)
												</th>
												<th className="pr-4 pb-3 text-left font-medium whitespace-nowrap text-muted-foreground">
													Consumption (kWh)
												</th>
												<th className="pr-4 pb-3 text-left font-medium whitespace-nowrap text-muted-foreground">
													Grid import (kWh)
												</th>
												<th className="pr-4 pb-3 text-left font-medium whitespace-nowrap text-muted-foreground">
													Grid export (kWh)
												</th>
												<th className="pr-4 pb-3 text-left font-medium whitespace-nowrap text-muted-foreground">
													Grid dependency
												</th>
												<th className="pb-3 text-left font-medium whitespace-nowrap text-muted-foreground">
													Daily value (USD)
												</th>
											</tr>
										</thead>
										<tbody>
											{rows
												.slice()
												.reverse()
												.map((row) => (
													<tr
														key={row.localDate}
														className="border-b border-border/70"
													>
														<td className="py-3 pr-4 tabular-nums">
															{row.localDate}
														</td>
														<td className="py-3 pr-4 tabular-nums">
															{toKwh(row.productionWh).toLocaleString()}
														</td>
														<td className="py-3 pr-4 tabular-nums">
															{row.consumptionWh === null
																? "n/a"
																: toKwh(row.consumptionWh).toLocaleString()}
														</td>
														<td className="py-3 pr-4 tabular-nums">
															{row.importedWh === null
																? "n/a"
																: toKwh(row.importedWh).toLocaleString()}
														</td>
														<td className="py-3 pr-4 tabular-nums">
															{row.exportedWh === null
																? "n/a"
																: toKwh(row.exportedWh).toLocaleString()}
														</td>
														<td className="py-3 pr-4 tabular-nums">
															{pct(row.gridDependencyRatio)}
														</td>
														<td className="py-3 tabular-nums">
															{toUsd(
																((row.productionWh -
																	Math.max(0, row.exportedWh ?? 0)) /
																	1000) *
																	GENERATED_RATE_PER_KWH +
																	(Math.max(0, row.exportedWh ?? 0) / 1000) *
																		EXPORTED_RATE_PER_KWH,
															)}
														</td>
													</tr>
												))}
										</tbody>
									</table>
								</div>
							</div>

							<div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
								<p className="hidden text-sm text-muted-foreground sm:block">
									Page {data.page} of {data.totalPages} ({data.totalRows} days)
								</p>
								<div className="flex items-center gap-2">
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => setPage((p) => Math.max(1, p - 1))}
										disabled={isLoadingPage || data.page <= 1}
									>
										Previous
									</Button>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() =>
											setPage((p) => Math.min(data.totalPages, p + 1))
										}
										disabled={isLoadingPage || data.page >= data.totalPages}
									>
										Next
									</Button>
								</div>
							</div>

							{chartMode === "money" ||
							(chartMode === "energy" && energyGranularity === "monthly") ? (
								<div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
									<p className="hidden text-sm text-muted-foreground sm:block">
										Months page {data.monthPage} of {data.monthTotalPages} (
										{data.totalMonths} months)
									</p>
									<div className="flex items-center gap-2">
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={() =>
												setMonthPage((p) =>
													Math.min(data.monthTotalPages, p + 1),
												)
											}
											disabled={
												isLoadingPage || data.monthPage >= data.monthTotalPages
											}
										>
											Older window
										</Button>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={() => setMonthPage((p) => Math.max(1, p - 1))}
											disabled={isLoadingPage || data.monthPage <= 1}
										>
											Newer window
										</Button>
									</div>
								</div>
							) : null}
						</section>
					) : (
						<div className="mt-12 rounded-xl border border-dashed border-border/70 bg-muted/20 p-10 text-center">
							<p className="text-base text-muted-foreground">
								No solar records yet. After the nightly cron runs (or a
								backfill), this page will populate automatically.
							</p>
						</div>
					)}
				</div>
			</main>
		</div>
	);
}
